import { graphqlClient } from '@/lib/graphql-client';
import { mergeWhere } from '@/lib/table-query';
import { FormDefinition } from '@/modules/shared/hr-form-engine';
import {
  buildReviewFilter,
  computeStepStatusAfterSubmit,
  findFlaggedAnswer,
  getPmReviewById,
  getPmReviewDetailById,
  getPmReviewDetails,
  getPmReviewDetailsForReview,
  getPmReviewsByIds,
  PmFormSnapshot,
  PmReview,
  PmReviewDetail,
  QueryParams,
  REVIEW_STATUS_FIELD,
  ReviewFilterValue,
  serializeFormSnapshot,
  updatePmReview,
  updatePmReviewDetail,
} from '@/modules/performance/hr-review-core';
import { buildGetMbmProjectsQuery } from '../graphql/queries';
import { MbmProjectOption, SubmissionRow } from '../types/submission.types';

export const getSubmissions = async (
  userId: string,
  filters: ReviewFilterValue,
  paging: Pick<QueryParams, 'pageNo' | 'pageSize' | 'order'> & {
    columnWhere?: Record<string, unknown>;
  }
): Promise<{ items: SubmissionRow[]; totalCount: number }> => {
  const { columnWhere, ...rest } = paging;
  const page = await getPmReviewDetails({
    ...rest,
    filter: mergeWhere(buildReviewFilter(filters, { PeerUserId: userId }), columnWhere),
  });
  // Sorted on the server, by `LastUpdatedDate` descending unless a column is sorted.
  const { items } = page;
  const reviews = await getPmReviewsByIds(items.map((detail) => detail.PmReviewId));
  const reviewById = new Map(reviews.map((review) => [review.ItemId, review]));
  return {
    items: items.map((detail) => ({ detail, review: reviewById.get(detail.PmReviewId) })),
    totalCount: page.totalCount,
  };
};

export interface SubmissionDetail {
  detail: PmReviewDetail;
  review: PmReview | null;
}

export const getSubmission = async (detailId: string): Promise<SubmissionDetail | null> => {
  const detail = await getPmReviewDetailById(detailId);
  if (!detail) return null;
  return { detail, review: await getPmReviewById(detail.PmReviewId) };
};

const PROJECT_PAGE_SIZE = 30;

export const searchMbmProjects = async (keyword: string): Promise<MbmProjectOption[]> => {
  const regex = { $regex: keyword, $options: 'i' };
  const filter = keyword ? { $or: [{ ProjectName: regex }, { CustomerName: regex }] } : {};
  const result = await graphqlClient.query<{ MbmProjects: { items: MbmProjectOption[] } }>({
    query: buildGetMbmProjectsQuery(filter, 1, PROJECT_PAGE_SIZE),
  });
  return result.MbmProjects.items;
};

export const getMbmProjectsByIds = async (projectIds: string[]): Promise<MbmProjectOption[]> => {
  if (projectIds.length === 0) return [];
  const result = await graphqlClient.query<{ MbmProjects: { items: MbmProjectOption[] } }>({
    query: buildGetMbmProjectsQuery({ ProjectId: { $in: projectIds } }, 1, projectIds.length),
  });
  return result.MbmProjects.items;
};

export interface SubmitReviewParams {
  detail: PmReviewDetail;
  snapshot: PmFormSnapshot;
  definition: FormDefinition;
  projectIds: string[];
  comment: string;
}

/**
 * Legacy UpdatePmReviewDetail, run client-side: stores the answered form, completes the reviewer's task, then moves the
 * parent review's step forward (a peer step completes only when every peer has submitted). The AI summary, MBM
 * hand-off, quantitative sync and notifications the legacy backend triggered afterwards are not run from here.
 */
export const submitReview = async ({
  detail,
  snapshot,
  definition,
  projectIds,
  comment,
}: SubmitReviewParams) => {
  const isAnnualReview = detail.AssessmentType === 'arf' && detail.ReviewType === 'annual';

  await updatePmReviewDetail(detail.ItemId, {
    PmFormJson: serializeFormSnapshot(snapshot, definition),
    ProjectIds: projectIds,
    UpdateComment: comment,
    Status: 'Completed',
    ...(isAnnualReview
      ? {
          SuggestedTier: findFlaggedAnswer(definition, 'IsPromotionTierQuestion'),
          PromotionRecommendation: findFlaggedAnswer(
            definition,
            'IsPromotionRecommendationQuestion'
          ),
        }
      : {}),
  });

  const peerDetails =
    detail.ReviewType === 'peer'
      ? await getPmReviewDetailsForReview(detail.PmReviewId, 'peer')
      : [];
  await updatePmReview(detail.PmReviewId, {
    [REVIEW_STATUS_FIELD[detail.ReviewType]]: computeStepStatusAfterSubmit(
      detail.ReviewType,
      peerDetails,
      detail.ItemId
    ),
  });
};
